/**
 * Removes all special characters,
 * leaving only alphanumeric characters and hyphens or spaces.
 * @param {string} inputString - The inputString parameter is a string that can contain special
 * characters.
 * @returns a modified version of the input string where all special characters (characters that are
 * not letters, numbers, or hyphens) have been removed.
 */
export const replaceSpecialCharactersButNotHyphenOrSpace = (
  inputString: string
): string => {
  return inputString.replace(/[^a-zA-Z0-9-\s ]/g, '');
};

/**
 * Replaces slashes and spaces in a given string with hyphens.
 * @param {string} inputString - The inputString parameter is a string that represents the input text
 * that you want to modify.
 * @returns a modified version of the input string where all slashes (/) and spaces are replaced with
 * hyphens (-).
 */
export const replaceSlashesAndSpaces = (inputString: string): string => {
  return inputString.replace(/[\/|\s]/g, '-');
};

/**
 * The function removes diacritics from a given string.
 * @param {string} inputString - The input string is the string from which you want to remove
 * diacritics. Diacritics are accents or other marks added to letters in some languages to indicate
 * pronunciation or to distinguish between different letters.
 * @returns The function `removeDiacritics` returns a string with diacritics (accent marks) removed
 * from the input string.
 */
export const removeDiacritics = (inputString: string): string => {
  return inputString.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

/**
 * The function replaces multiple dashes in a string with a single dash.
 * @param {string} inputString - The inputString parameter is a string that represents the input text
 * that you want to modify.
 * @returns a string.
 */
export const replaceMultipleDashes = (inputString: string): string => {
  return inputString.replace(/-+/g, '-');
};

/**
 * The `trimDashes` function removes dashes from the beginning and end of a string.
 * @param {string} inputString - A string that may contain dashes at the beginning or end.
 * @returns The function `trimDashes` returns a string.
 */
export const trimDashes = (inputString: string): string => {
  return inputString.replace(/^-+|-+$/g, '');
};

/**
 * Given a string, removes the trailing slash, if there is one.
 * @param {string} url
 * @returns
 */
export const trimTrailingSlash = (url: string): string =>
  url ? url.replace(/\/$/, '') : '';
/**
 * Given a string, removes the leading slash, if there is one.
 * @param {string} url
 * @returns
 */
export const trimLeadingSlash = (url: string): string =>
  url ? url.replace(/^\/+/g, '') : '';

/**
 * Given a path, removes both trailing and leading slash.
 * @param {string} url
 * @returns
 */
export const trimSlash = (url: string): string =>
  url ? url.replace(/^\/+|\/+$/g, '') : '';

/**
 * Joins multiple URL parts into a single URL string, ensuring proper formatting.
 * @param {...(string | undefined | null)[]} parts - The URL parts to join.
 * @returns {string} - The joined URL string.
 * @example
 * // Example usage:
 * const url = joinUrlParts('https://example.com', 'path', 'to', 'resource');
 * console.log(url); // Output: "https://example.com/path/to/resource"
 * const urlWithTrailingSlash = joinUrlParts('https://example.com/', '/path', 'to', 'resource');
 * console.log(urlWithTrailingSlash); // Output: "https://example.com/path/to/resource"
 */
export function joinUrlParts(...parts: (string | undefined | null)[]): string {
  // Remove all falsy values and any standalone '/' values
  const filtered = parts.filter((part) => Boolean(part) && part !== '/');
  if (filtered.length === 0) return '';
  if (filtered.length === 1) return String(filtered[0]);

  const trimmed = filtered.map((part, i) => {
    let str = String(part);
    if (i === 0) return str.replace(/\/+$/, ''); // Only trim trailing slashes for first part
    return str.replace(/^\/+/, '').replace(/\/+$/, ''); // Trim both for rest
  });
  return trimmed.join('/');
}

/**
 * The `removeStopWords` function removes predefined stop words from the string.
 * @param {string} inputString - A string that may contain stop words.
* @param {string} splitChar - Character used to split the input string before removing stop words.
* @param {string} joinChar - Character used to join the input string after removing stop words.

 * @returns The function `removeStopWords` returns a string.
 */
export const removeStopWords = (
  inputString: string,
  splitChar: string = '-',
  joinChar: string = '-'
): string => {
  const _stopWords: Set<string> = new Set([
    'a',
    'an',
    'and',
    'are',
    'as',
    'at',
    'be',
    'because',
    'but',
    'can',
    'did',
    'does',
    'else',
    'for',
    'from',
    'get',
    'got',
    'his',
    'if',
    'in',
    'into',
    'is',
    'me',
    'my',
    'or',
    'out',
    'so',
    'statistics',
    'the',
    'there',
    'to',
    'we',
    'when',
    'it',
  ]);

  // Split title into an array of parts
  const slugParts: string[] = inputString
    .split(splitChar)
    .filter((part) => part.trim() !== '');

  // Remove stop words from the array
  const parsedParts: string[] = slugParts.filter(
    (part) => !_stopWords.has(part.toLowerCase())
  );

  // Form the initial slug from the parsed array, separated with dashes
  return parsedParts.join(joinChar);
};

/**
 * Removes all spaces and non-alphanumeric characters from the input string.
 *
 * @param inputString - The string to be cleaned.
 * @returns The cleaned string with only alphanumeric characters.
 *
 * @example
 * // Example usage:
 * const result1 = removeSpacesAndNonAlphaNumeric("Hello, World!"); // "HelloWorld"
 * const result2 = removeSpacesAndNonAlphaNumeric("123-456_789");   // "123456789"
 * const result3 = removeSpacesAndNonAlphaNumeric("A@B#C$D%E^F&G*"); // "ABCDEFG"
 */
export const removeSpacesAndNonAlphaNumeric = (inputString: string): string => {
  if (inputString === null || inputString === undefined) {
    return '';
  }
  return inputString.replace(/[^a-zA-Z0-9]/g, '');
};
